import { useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { canUseChangeControl } from '../changes/changeControl';

export const PE_BASE = '/quality/pe-actions';
export const BOARDS = {
  'task-follow-up': { title: 'PE Task Follow-Up', path: '/quality/pe-task-follow-up', cadence: 'Weekly meeting',
    description: 'Immediate production actions, project follow-up and engineering change blockers.' },
  'production-improvement': { title: 'PE Production Improvement', path: '/quality/pe-production-improvement', cadence: 'Biweekly meeting',
    description: 'Continuous improvement of production lines, with a measurable baseline, target and result.' },
};
export const STATUSES = ['Open', 'In Progress', 'Ready for Verification', 'Closed'];
export const PRIORITIES = ['Low', 'Normal', 'High', 'Critical'];
export const AREAS = [['kaizen', 'Kaizen'], ['dfma', 'DFMA'], ['capacity', 'Capacity'],
  ['process-yield-cost', 'Process yield & cost'], ['flow-layout', 'Flow & layout'], ['repeatability-testers', 'Repeatability & testers']];
export const CONTENT_KEYS = ['title', 'description', 'nextAction', 'productFamily', 'productNumbers', 'line', 'site', 'priority',
  'isBlocking', 'ownerNI', 'ownerGPV', 'dueDate', 'supplierKey', 'sharedWithSupplier', 'references', 'attachments', 'improvement'];
export const PROPOSAL_KEYS = ['title', 'description', 'nextAction', 'productFamily', 'productNumbers', 'line', 'site', 'improvement'];
export function peAccessForUser(user) {
  const metadata = user?.publicMetadata || {};
  const roles = [metadata.role, metadata.roles].flat().filter(role => typeof role === 'string').map(role => role.trim().toLowerCase().replace(/[\s_]+/g, '-'));
  const staff = canUseChangeControl(user), supplier = roles.some(role => ['gpv', 'test-gpv'].includes(role));
  return { staff, supplier, allowed: staff || supplier };
}
export function peContent(record = {}) {
  return Object.fromEntries(CONTENT_KEYS.filter(key => record[key] !== undefined).map(key => [key, JSON.parse(JSON.stringify(record[key]))]));
}
export function newPeContent() {
  return { title: '', description: '', nextAction: '', productFamily: '', productNumbers: [], line: '', site: '', priority: 'Normal',
    isBlocking: false, ownerNI: '', ownerGPV: '', dueDate: null, supplierKey: 'gpv', sharedWithSupplier: false, references: [], attachments: [],
    improvement: { area: '', metric: '', baseline: '', target: '', result: '' } };
}
export function reconcilePeContent(previous, local, saved) {
  const base = { ...newPeContent(), ...peContent(previous) }, current = { ...newPeContent(), ...peContent(local) };
  const merged = { ...newPeContent(), ...peContent(saved) }, conflicts = [];
  for (const key of CONTENT_KEYS) {
    if (JSON.stringify(current[key]) === JSON.stringify(base[key])) continue;
    if (JSON.stringify(merged[key]) !== JSON.stringify(base[key]) && JSON.stringify(current[key]) !== JSON.stringify(merged[key])) conflicts.push({ key, saved: merged[key], local: current[key] });
    merged[key] = current[key];
  }
  return { content: merged, conflicts };
}
export const dateOnly = value => value ? String(value).slice(0, 10) : '';
export function dateTime(value) { return value ? new Date(value).toLocaleString() : '—'; }
export function isOverdue(record) {
  const today = new Date(); const local = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return record.status !== 'Closed' && !!record.dueDate && dateOnly(record.dueDate) < local;
}
export function usePeApi() {
  const { getToken } = useAuth();
  return useCallback(async (path = '', options = {}) => {
    const token = await getToken();
    if (!token) throw new Error('Sign in again to continue.');
    const multipart = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const response = await window.fetch(`${(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '')}/quality/pe-actions${path}`, {
      ...options, headers: { Authorization: `Bearer ${token}`, ...(!multipart && options.body ? { 'Content-Type': 'application/json' } : {}), ...options.headers },
      ...(options.body ? { body: multipart ? options.body : JSON.stringify(options.body) } : {}),
    });
    let data;
    try { data = await response.json(); } catch { throw new Error(`The server returned an unreadable response (${response.status}). Your input is kept.`); }
    if (!response.ok || data.ok === false) {
      const error = new Error(data.message || data.error || `Request failed (${response.status}).`);
      error.status = response.status; error.data = data; throw error;
    }
    return data;
  }, [getToken]);
}
